import { createFileRoute } from '@tanstack/react-router';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Button } from '@tremor/react';
import { Trans } from '@lingui/macro';

import { Wrapper, ArticleForm } from '@/components';
import { handleFormError } from '@/utils';
import { useCreateArticle, getArticlesQueryKey, useAudiences } from '@/api';
import type { ArticleCrud } from '@/api/model';
import { articleSchema } from '@/schemas';

export const Route = createFileRoute('/_authenticated/articles/new')({
  component: NewArticle,
});

function NewArticle() {
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext({
    select: ({ queryClient }) => ({ queryClient }),
  });
  const { mutateAsync } = useCreateArticle({
    mutation: {
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: getArticlesQueryKey(),
        });
      },
    },
  });
  const { data: audienceData } = useAudiences();
  const { audiences = [] } = audienceData ?? {};

  const methods = useForm<ArticleCrud>({
    resolver: zodResolver(articleSchema),
  });

  const onSave = methods.handleSubmit(async (data) => {
    try {
      await mutateAsync({ data });
      methods.reset();
      toast.success(<Trans>Article created successfully!</Trans>);
      await navigate({ to: '/articles' });
    } catch (error) {
      handleFormError(error, methods.setError);
    }
  });

  return (
    <Wrapper
      header={<Trans>New article</Trans>}
      backLink='/articles'
      extraActions={
        <Button onClick={onSave}>
          <Trans>Save</Trans>
        </Button>
      }
    >
      <FormProvider {...methods}>
        <ArticleForm audiences={audiences} />
      </FormProvider>
    </Wrapper>
  );
}
